/* eslint-disable @typescript-eslint/no-explicit-any */
// SubscriptionManager.ts
// Centralizes all Supabase Realtime subscriptions (annotations, images, presence)
// - Single channel per topic
// - Type-safe event payloads
// - Local-echo suppression to avoid bouncing your own writes
// - Lightweight backoff on (re)subscribe
// - Presence with selection tracking (image_id) and computed byImage map

import { supabase } from "@/supabase";
import type { RealtimeChannel, RealtimePostgresChangesPayload } from "@supabase/supabase-js";
import type { Line, ImageInfo } from "@/helpers/Interfaces";

type Topic = string;

// ----- Topics helpers (public) -----
function topicAnnotations(imageId: number): Topic {
  return `annotations:${imageId}`;
}
function topicImages(projectId: number): Topic {
  return `images:${projectId}`;
}
function topicPresence(projectId: number): Topic {
  return `presence:${projectId}`;
}

// ----- Event maps -----
type AnnotationInsert = { line: Line };
type AnnotationUpdate = { line: Line };
type AnnotationDelete = { id: number };

type ImageInsert = { image: ImageInfo };
type ImageUpdate = { image: ImageInfo };
type ImageDelete = { image: ImageInfo };
type ImageValidated = { image: ImageInfo };
type ImageInvalidated = { image: ImageInfo };
type ImageHasLines = { image: ImageInfo };
type ImageLock = { image: ImageInfo };

type PresenceUpdate = {
  byImage: Record<number, Array<{ key: string; email: string }>>;
};

type EventName =
  | "annotations:insert"
  | "annotations:update"
  | "annotations:delete"
  | "images:insert"
  | "images:update"
  | "images:delete"
  | "images:validated"
  | "images:invalidated"
  | "images:has_lines"
  | "images:lock"
  | "presence:update";

type EventPayloadMap = {
  "annotations:insert": AnnotationInsert;
  "annotations:update": AnnotationUpdate;
  "annotations:delete": AnnotationDelete;

  "images:insert": ImageInsert;
  "images:update": ImageUpdate;
  "images:delete": ImageDelete;
  "images:validated": ImageValidated;
  "images:invalidated": ImageInvalidated;
  "images:has_lines": ImageHasLines;
  "images:lock": ImageLock;

  "presence:update": PresenceUpdate;
};

type Listener<E extends EventName> = (payload: EventPayloadMap[E]) => void;

// ----- Internal state -----
class SubscriptionManager {
  // One Supabase channel per topic
  private channels = new Map<Topic, RealtimeChannel>();

  // Listener sets per topic+event
  private listeners = new Map<Topic, Map<EventName, Set<Function>>>();

  // Local echo ids to suppress self-bounce (per topic)
  private localEcho = new Map<Topic, Set<number>>();

  // Presence metadata (selection) and my identity
  private presenceMyUserId: string | null = null;
  private presenceMyEmail: string | null = null;
  private presenceSessionId: string =
    (globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2));

  // Debounce presence emit per topic
  private presenceEmitTimer = new Map<Topic, number>();

  // ----- Public helpers (topics) -----
  public topicAnnotations = topicAnnotations;
  public topicImages = topicImages;
  public topicPresence = topicPresence;

  // ----- Public: mark local echo -----
  // Use this right after a successful mutation so the next realtime event is ignored.
  public markLocalEcho(topic: Topic, id: number) {
    if (!id) return;
    const set = this.localEcho.get(topic) ?? new Set<number>();
    set.add(id);
    this.localEcho.set(topic, set);

    // Optional cleanup after a while
    window.setTimeout(() => set.delete(id), 6000);
  }

  // ----- Public: subscribe -----
  // Ensures the Supabase channel is created and hooked once per topic.
  // Returns an unsubscribe function for just this listener.
  public async subscribe<E extends EventName>(
    topic: Topic,
    event: E,
    fn: Listener<E>
  ): Promise<() => void> {
    await this.ensureChannel(topic);
    const map = this.listeners.get(topic) ?? new Map<EventName, Set<Function>>();
    const set = (map.get(event) ?? new Set<Function>()) as Set<Function>;
    set.add(fn as any);
    map.set(event, set);
    this.listeners.set(topic, map);

    // returns an unsubscribe for this listener
    return () => {
      const map = this.listeners.get(topic);
      if (!map) return;
      const set = map.get(event);
      if (!set) return;
      set.delete(fn as any);
      if (set.size === 0) map.delete(event);
      if (map.size === 0) {
        // no more listeners -> optionally close the channel (keep open if presence needs it)
        // For safety we keep the channel alive; uncomment to aggressively close:
        // this.teardownTopic(topic).catch(() => {});
      }
    };
  }

  // ----- Public: presence selection (image id per project) -----
  // Call whenever the user selects a different image in the project.
  public async presenceSetSelection(projectId: number, imageId: number | null) {
    const topic = topicPresence(projectId);
    await this.ensureChannel(topic, { isPresence: true });

    const ch = this.channels.get(topic) as any;
    if (!ch || !this.presenceMyUserId) return;

    await ch.track({
      key: this.presenceMyUserId,
      email: this.presenceMyEmail,
      image_id: imageId,
      session_id: this.presenceSessionId,
    });

    // Emit fresh map immediately (also presence sync will update it)
    this.emitPresence(topic);
  }

  // ----- Internal: ensure a supabase channel exists and is wired -----
  private async ensureChannel(topic: Topic, opts?: { isPresence?: boolean }) {
    if (this.channels.has(topic)) return;

    const chan = this.createChannelForTopic(topic, !!opts?.isPresence);
    this.channels.set(topic, chan);

    const ok = await this.waitSubscribe(chan);
    if (!ok) {
      console.warn("[SubscriptionManager] could not subscribe:", topic);
    }

    // extra warm-up for presence
    if (topic.startsWith("presence:")) {
      await this.ensureAuthIdentity();
      // Track initial (null) so everyone sees you connected
      await (chan as any).track({
        key: this.presenceMyUserId,
        email: this.presenceMyEmail,
        image_id: null,
        session_id: this.presenceSessionId,
      }).catch(() => {});
      // Emit initial state
      this.emitPresence(topic);
    }
  }

  // ----- Internal: create channel & wire handlers per topic family -----
  private createChannelForTopic(topic: Topic, isPresence = false): RealtimeChannel {
    if (topic.startsWith("annotations:")) {
      const imageId = Number(topic.split(":")[1]);
      const ch = supabase
        .channel(`rt:annotations:${imageId}`)
        .on(
          "postgres_changes",
          { schema: "public", table: "annotations", event: "INSERT", filter: `image_id=eq.${imageId}` },
          (payload) => this.onAnnotationInsert(topic, payload)
        )
        .on(
          "postgres_changes",
          { schema: "public", table: "annotations", event: "UPDATE", filter: `image_id=eq.${imageId}` },
          (payload) => this.onAnnotationUpdate(topic, payload)
        )
        .on(
          "postgres_changes",
          { schema: "public", table: "annotations", event: "DELETE", filter: `image_id=eq.${imageId}` },
          (payload) => this.onAnnotationDelete(topic, payload)
        );
      return ch;
    }

    if (topic.startsWith("images:")) {
      const projectId = Number(topic.split(":")[1]);
      const ch = supabase
        .channel(`rt:images:${projectId}`)
        .on(
          "postgres_changes",
          { schema: "public", table: "project_images", event: "INSERT", filter: `project_id=eq.${projectId}` },
          (payload) => this.onImageInsert(topic, payload)
        )
        .on(
          "postgres_changes",
          { schema: "public", table: "project_images", event: "DELETE", filter: `project_id=eq.${projectId}` },
          (payload) => this.onImageDelete(topic, payload)
        )
        .on(
          "postgres_changes",
          { schema: "public", table: "project_images", event: "UPDATE", filter: `project_id=eq.${projectId}` },
          (payload) => this.onImageUpdate(topic, payload)
        );
      return ch;
    }

    if (topic.startsWith("presence:")) {
      const projectId = Number(topic.split(":")[1]);
      const ch = supabase.channel(`rt:presence:${projectId}`, {
        config: { presence: { key: this.presenceMyUserId ?? "anonymous" } },
      });

      // Debounced re-emit on any presence change
      ch.on("presence", { event: "sync" }, () => this.schedulePresenceEmit(topic))
        .on("presence", { event: "join" }, () => this.schedulePresenceEmit(topic))
        .on("presence", { event: "leave" }, () => this.schedulePresenceEmit(topic));

      return ch;
    }

    // Fallback to a plain channel (should not happen)
    return supabase.channel(topic);
  }

  // ----- Internal: subscribe with micro backoff -----
  private async waitSubscribe(ch: RealtimeChannel, attempts = 3): Promise<boolean> {
    for (let i = 1; i <= attempts; i++) {
      const ok = await new Promise<boolean>((resolve) => {
        let settled = false;
        const to = setTimeout(() => {
          if (!settled) { settled = true; resolve(false); }
        }, 5000);
        ch.subscribe((status) => {
          if (settled) return;
          if (status === "SUBSCRIBED") {
            clearTimeout(to);
            settled = true;
            resolve(true);
          } else if (status === "TIMED_OUT" || status === "CHANNEL_ERROR") {
            clearTimeout(to);
            settled = true;
            resolve(false);
          }
        });
      });
      if (ok) return true;

      try { await ch.unsubscribe(); } catch {}
      await new Promise(r => setTimeout(r, 120 * i));
      // resubscribe attempt
      try {
        const res = await new Promise<boolean>((resolve) => {
          let settled = false;
          const to = setTimeout(() => {
            if (!settled) { settled = true; resolve(false); }
          }, 5000);
          ch.subscribe((status) => {
            if (settled) return;
            if (status === "SUBSCRIBED") { clearTimeout(to); settled = true; resolve(true); }
            else if (status === "TIMED_OUT" || status === "CHANNEL_ERROR") { clearTimeout(to); settled = true; resolve(false); }
          });
        });
        if (res) return true;
      } catch {}
    }
    return false;
  }

  // ----- Internal: emit helpers -----
  private emit<E extends EventName>(topic: Topic, event: E, payload: EventPayloadMap[E]) {
    const byEvent = this.listeners.get(topic);
    if (!byEvent) return;
    const set = byEvent.get(event);
    if (!set || set.size === 0) return;
    // fan out
    for (const fn of set) {
      try { (fn as Listener<E>)(payload); } catch (e) {
        console.warn(`[SubscriptionManager] listener error (${topic}/${event}):`, e);
      }
    }
  }

  // ----- Internal: annotation handlers (with local-echo suppression) -----
  private onAnnotationInsert(topic: Topic, payload: RealtimePostgresChangesPayload<any>) {
    const row = payload.new as { id: number; data?: any, is_hidden?: boolean; };
    const line = { id: row.id, ...(row?.data ?? {}), is_hidden: row?.is_hidden ?? false } as Line;

    // Local echo: if marked, skip and clear
    if (this.localEcho.get(topic)?.has(line.id)) {
      this.localEcho.get(topic)?.delete(line.id);
      return;
    }
    this.emit(topic, "annotations:insert", { line });
  }

  private onAnnotationUpdate(topic: Topic, payload: RealtimePostgresChangesPayload<any>) {
    const row = payload.new as { id: number; data?: any, is_hidden?: boolean; };
    const line = { id: row.id, ...(row?.data ?? {}), is_hidden: row?.is_hidden ?? false } as Line;

    if (this.localEcho.get(topic)?.has(line.id)) {
      this.localEcho.get(topic)?.delete(line.id);
      return;
    }
    this.emit(topic, "annotations:update", { line });
  }

  private onAnnotationDelete(topic: Topic, payload: RealtimePostgresChangesPayload<any>) {
    const id = (payload.old as any)?.id as number;
    if (!id && id !== 0) return;

    if (this.localEcho.get(topic)?.has(id)) {
      this.localEcho.get(topic)?.delete(id);
      return;
    }
    this.emit(topic, "annotations:delete", { id });
  }

  // ----- Internal: images handlers (with derived events) -----
  private onImageInsert(topic: Topic, payload: RealtimePostgresChangesPayload<any>) {
    const image = payload.new as ImageInfo;
    if (!image?.id) return;
    if (this.localEcho.get(topic)?.has(image.id)) {
      this.localEcho.get(topic)?.delete(image.id);
      return;
    }
    this.emit(topic, "images:insert", { image });
  }

  private onImageDelete(topic: Topic, payload: RealtimePostgresChangesPayload<any>) {
    const image = payload.old as ImageInfo;
    if (!image?.id) return;
    if (this.localEcho.get(topic)?.has(image.id)) {
      this.localEcho.get(topic)?.delete(image.id);
      return;
    }
    this.emit(topic, "images:delete", { image });
  }

  private onImageUpdate(topic: Topic, payload: RealtimePostgresChangesPayload<any>) {
    const n = payload.new as ImageInfo;
    const o = payload.old as ImageInfo;
    if (!n?.id) return;

    if (this.localEcho.get(topic)?.has(n.id)) {
      this.localEcho.get(topic)?.delete(n.id);
      return;
    }

    // Always emit generic update
    this.emit(topic, "images:update", { image: n });

    // Derived events (edge-triggered)
    if (n.has_lines !== o.has_lines) this.emit(topic, "images:has_lines", { image: n });
    if (n.is_validated !== o.is_validated) this.emit(topic, "images:validated", { image: n });
    if (n.is_invalidated !== o.is_invalidated || n.invalidate_notes !== o.invalidate_notes) {
      this.emit(topic, "images:invalidated", { image: n });
    }
    if (n.review_lock_by !== o.review_lock_by || n.review_lock_until !== o.review_lock_until) {
      this.emit(topic, "images:lock", { image: n });
    }
  }

  // ----- Internal: presence helpers -----
  private schedulePresenceEmit(topic: Topic) {
    const prev = this.presenceEmitTimer.get(topic);
    if (prev) window.clearTimeout(prev);
    const tid = window.setTimeout(() => this.emitPresence(topic), 60);
    this.presenceEmitTimer.set(topic, tid);
  }

  private emitPresence(topic: Topic) {
    const ch = this.channels.get(topic) as any;
    if (!ch) return;

    const raw = ch.presenceState?.() as Record<string, Array<any>> | undefined;
    const byImage: Record<number, Array<{ key: string; email: string }>> = {};
    if (raw) {
      for (const [key, metas] of Object.entries(raw)) {
        for (const meta of metas) {
          const isSelf =
            this.presenceMyUserId &&
            key === this.presenceMyUserId &&
            meta?.session_id === this.presenceSessionId;
          if (isSelf) continue;

          const imageId = meta?.image_id ?? null;
          const email = meta?.email ?? "";
          if (imageId == null) continue;

          if (!byImage[imageId]) byImage[imageId] = [];
          // Ensure uniqueness per user key
          if (!byImage[imageId].some(u => u.key === key)) {
            byImage[imageId].push({ key, email });
          }
        }
      }
      Object.keys(byImage).forEach(id => {
        byImage[+id].sort((a, b) => (a.email || "").localeCompare(b.email || ""));
      });
    }

    this.emit(topic, "presence:update", { byImage });
  }

  private async ensureAuthIdentity() {
    if (this.presenceMyUserId) return;
    const { data: { session } } = await supabase.auth.getSession();
    this.presenceMyUserId = session?.user?.id ?? null;
    this.presenceMyEmail = session?.user?.email ?? null;
  }

  // ----- Optional aggressive teardown (unused) -----
  private async teardownTopic(topic: Topic) {
    const ch = this.channels.get(topic);
    if (!ch) return;
    try { await ch.unsubscribe(); } catch {}
    try { supabase.removeChannel(ch as any); } catch {}
    this.channels.delete(topic);
  }
}

export const subscriptionManager = new SubscriptionManager();
export type { Topic, EventName, EventPayloadMap };
